/**
 * Presence context — pings /api/presence/heartbeat every 30s while authenticated,
 * exposes the set of currently-online buddy ids, and toasts when a buddy newly
 * comes online (max once per hour per buddy, in-memory cooldown).
 *
 * Backend handles its own dedup of `buddy_online` notifications in the bell.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const PresenceContext = createContext({ onlineBuddyIds: new Set() });
const HEARTBEAT_MS = 30 * 1000;
const TOAST_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour per buddy

export function PresenceProvider({ children }) {
  const { user, api } = useAuth();
  const [onlineBuddyIds, setOnlineBuddyIds] = useState(new Set());
  const previousIdsRef = useRef(new Set());
  const lastToastedRef = useRef(new Map()); // userId -> timestamp ms
  const initialFetchRef = useRef(false);

  const heartbeat = useCallback(async () => {
    try {
      const res = await api().post('/presence/heartbeat');
      const list = res.data?.online_buddies || [];
      const ids = new Set(list.map((b) => b.id));

      // Toast on transitions: someone in `ids` who wasn't in previousIdsRef
      if (initialFetchRef.current) {
        const now = Date.now();
        for (const b of list) {
          if (!previousIdsRef.current.has(b.id)) {
            const last = lastToastedRef.current.get(b.id) || 0;
            if (now - last > TOAST_COOLDOWN_MS) {
              toast.success(`${b.name || 'Your buddy'} is online`, { icon: '🟢' });
              lastToastedRef.current.set(b.id, now);
            }
          }
        }
      } else {
        initialFetchRef.current = true;
      }
      previousIdsRef.current = ids;
      setOnlineBuddyIds(ids);
    } catch {
      /* network blip — try again next tick */
    }
  }, [api]);

  useEffect(() => {
    if (!user) {
      setOnlineBuddyIds(new Set());
      previousIdsRef.current = new Set();
      initialFetchRef.current = false;
      return undefined;
    }
    heartbeat();
    const t = setInterval(heartbeat, HEARTBEAT_MS);
    const onFocus = () => heartbeat();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, heartbeat]);

  return (
    <PresenceContext.Provider value={{ onlineBuddyIds, heartbeat }}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence() {
  return useContext(PresenceContext);
}

export function isUserOnline(userId, onlineBuddyIds) {
  if (!userId || !onlineBuddyIds) return false;
  return onlineBuddyIds.has(userId);
}
